import * as yup from 'yup';
import { Eye, EyeOff, Trash2 } from 'react-feather';
import { FormManager, useForm } from 'react-form-state-manager';
import { TypedDocumentNode } from '@apollo/client';
import { addDays, addMinutes, differenceInMinutes } from 'date-fns';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import keyBy from 'lodash/keyBy';
import takeRight from 'lodash/takeRight';

import { GetTicketQuery as GetTicket } from '__generated__/graphql';

import { ErrorBag, getServerErrors } from '../../../../common/helpers';
import { TranslatableInput } from '../../../Translations/Inputs';
import { translationsForAttribute } from '../../../Translations/helpers';
import GetEventLayoutQuery from '../GetEventLayoutQuery';
import UI from '../../../../common/UI';
import useEditTicketMutation from './useEditTicketMutation';
import useIntegerInput from '../../../../common/useIntegerInput';
import useLocale from '../../../../common/useLocale';
import useMediaDevice from '../../../../common/useMediaDevice';
import useNavigationPrompt from '../../../../common/useNavigationPrompt';
import useProject from '../../../useProject';
import useTouchOnChange from '../../../../common/useTouchOnChange';
import useValidation from '../../../../common/useValidation';

type Event = GetTicket['event'];
type Ticket = GetTicket['ticket'];
type TimeSlot = Ticket['time_slots'][0];

interface TimeSlotFormProps {
  form: FormManager;
  index: number;
  ticket: Ticket;
  timeSlot?: TimeSlot;
  showReservations: boolean;
  errors: ErrorBag;
  toggleTimeSlot: () => void;
  deleteTimeSlot: () => void;
}

export interface TimeSlotData {
  id?: string | null;
  start_date: string | null;
  start_time: string | null;
  capacity: number | null;
}

const DesktopTimeSlotForm = (
  {
    form,
    index,
    errors,
    ticket,
    timeSlot,
    showReservations,
    toggleTimeSlot,
    deleteTimeSlot,
  }: TimeSlotFormProps,
) => {
  const { t } = useTranslation();
  const { formatInteger } = useLocale();
  const integerInput = useIntegerInput();

  const key = (k?: string) => `time_slots.${index}${k ? `.${k}` : ''}`;

  useTouchOnChange(form, key('start_date'));
  useTouchOnChange(form, key('start_time'));

  return (
    <UI.FormGrid sc={{
      columns: showReservations ? '3fr 2fr 3fr 2fr 2fr 2fr 66px' : '3fr 2fr 3fr 2fr 2fr 66px',
      gutter: 0.5,
    }}
    >
      <UI.InputGroup sc={{ valid: !errors[key('start_date')], touched: form.getTouched(key('start_date')) }}>
        <UI.DatePicker
          selected={form.get(key('start_date'))}
          onChange={(value) => form.set(key('start_date'), value)}
          selectsStart
          id={`StartDate_${index}`}
        />
        <UI.ErrorMessages
          attribute={t('start_date')}
          errors={errors[key('start_date')]}
        />
      </UI.InputGroup>

      <UI.InputGroup sc={{ valid: !errors[key('start_time')], touched: form.getTouched(key('start_time')) }}>
        <UI.TimePicker
          selected={form.get(key('start_time'))}
          onChange={(value) => form.set(key('start_time'), value)}
          timeIntervals={5}
          id={`StartTime_${index}`}
        />
        <UI.ErrorMessages
          attribute={t('start_time')}
          errors={errors[key('start_time')]}
        />
      </UI.InputGroup>

      <UI.InputGroup sc={{ valid: !errors[key('capacity')], touched: form.getTouched(key('capacity')) }}>
        <TranslatableInput
          form={form}
          name={key('title')}
          id={`Title_${index}`}
          defaultLocale={ticket.locale}
          title={t('title')}
          aria-label={t('title')}
          maxLength={100}
        />
        <UI.ErrorMessages
          attribute={t('title')}
          errors={errors[key('title')]}
        />
      </UI.InputGroup>

      <UI.InputGroup sc={{ valid: !errors[key('capacity')], touched: form.getTouched(key('capacity')) }}>
        <UI.DebouncedInput
          {...form.text(key('capacity'), { inputHandler: integerInput() })}
          format={integerInput().format}
          maxLength={10}
          inputMode="numeric"
          match={/[\d]/g}
          placeholder="∞"
          id={`Capacity_${index}`}
          aria-label={t('capacity')}
        />
        <UI.ErrorMessages
          attribute={t('capacity')}
          errors={errors[key('capacity')]}
        />
      </UI.InputGroup>

      <UI.Div sc={{ pt: 1.5 }}>
        {timeSlot ? formatInteger(timeSlot.sold_count) : 0}
      </UI.Div>

      {showReservations && (
        <UI.Div sc={{ pt: 1.5 }}>
          {timeSlot ? formatInteger(timeSlot.invitation_reservations_count) : 0}
        </UI.Div>
      )}

      <UI.Div sc={{ pt: 0.75, textAlign: 'right' }}>
        <UI.Button
          onClick={() => toggleTimeSlot()}
          sc={{ blank: true, size: 'sm', color: 'gray.400' }}
          aria-label={form.get(key('active')) ? t('disable') : t('enable')}
        >
          <UI.Icon>
            {form.get(key('active')) ? <Eye /> : <EyeOff />}
          </UI.Icon>
        </UI.Button>
        <UI.Button
          onClick={() => deleteTimeSlot()}
          sc={{ blank: true, size: 'sm', color: 'gray.400' }}
          disabled={timeSlot && !timeSlot.deletable}
        >
          <UI.Icon>
            <Trash2 />
          </UI.Icon>
        </UI.Button>
      </UI.Div>
    </UI.FormGrid>
  );
};

const MobileTimeSlotForm = (
  {
    form,
    ticket,
    timeSlot,
    showReservations,
    index,
    errors,
    toggleTimeSlot,
    deleteTimeSlot,
  }: TimeSlotFormProps,
) => {
  const { t } = useTranslation();
  const { formatInteger } = useLocale();
  const integerInput = useIntegerInput();

  const key = (k?: string) => `time_slots.${index}${k ? `.${k}` : ''}`;

  useTouchOnChange(form, key('start_date'));
  useTouchOnChange(form, key('start_time'));

  return (
    <UI.Div sc={{ background: 'gray.100', padding: 4 }}>
      <UI.FormGrid sc={{ column: 'span 2' }}>
        <UI.InputGroup sc={{ valid: !errors[key('start_date')], touched: form.getTouched(key('start_date')) }}>
          <UI.InputLabel htmlFor={`StartDate_${index}`}>
            {t('start_date')}
            {' '}
            <UI.RequiredMark />
          </UI.InputLabel>
          <UI.DatePicker
            selected={form.get(key('start_date'))}
            onChange={(value) => form.set(key('start_date'), value)}
            selectsStart
            id={`StartDate_${index}`}
          />
          <UI.ErrorMessages
            attribute={t('start_date')}
            errors={errors[key('start_date')]}
          />
        </UI.InputGroup>

        <UI.InputGroup sc={{ valid: !errors[key('start_time')], touched: form.getTouched(key('start_time')) }}>
          <UI.InputLabel htmlFor={`StartTime_${index}`}>
            {t('start_time')}
          </UI.InputLabel>
          <UI.TimePicker
            selected={form.get(key('start_time'))}
            onChange={(value) => form.set(key('start_time'), value)}
            timeIntervals={5}
            id={`StartTime_${index}`}
          />
          <UI.ErrorMessages
            attribute={t('start_time')}
            errors={errors[key('start_time')]}
          />
        </UI.InputGroup>

        <UI.InputGroup sc={{ valid: !errors[key('title')], touched: form.getTouched(key('title')) }}>
          <UI.InputLabel htmlFor={`Title_${index}`}>
            {t('title')}
          </UI.InputLabel>
          <TranslatableInput
            form={form}
            name={key('title')}
            id={`Title_${index}`}
            defaultLocale={ticket.locale}
            title={t('title')}
            maxLength={100}
          />
          <UI.ErrorMessages
            attribute={t('title')}
            errors={errors[key('title')]}
          />
        </UI.InputGroup>

        <UI.InputGroup sc={{ valid: !errors[key('capacity')], touched: form.getTouched(key('capacity')) }}>
          <UI.InputLabel htmlFor={`Capacity_${index}`}>
            {t('capacity')}
          </UI.InputLabel>
          <UI.DebouncedInput
            {...form.text(key('capacity'), { inputHandler: integerInput() })}
            format={integerInput().format}
            maxLength={10}
            inputMode="numeric"
            match={/[\d]/g}
            id={`Capacity_${index}`}
            placeholder="∞"
          />
          <UI.ErrorMessages
            attribute={t('capacity')}
            errors={errors[key('capacity')]}
          />
        </UI.InputGroup>

        <UI.FormGrid sc={{ columns: showReservations ? '1fr 1fr 1fr' : '1fr 1fr', gutter: 0.25 }}>
          <UI.Div sc={{ pt: 1, small: true }}>
            {`${t('registrations')}: `}
            {timeSlot ? formatInteger(timeSlot.sold_count) : 0}
          </UI.Div>

          {showReservations && (
            <UI.Div sc={{ pt: 1, small: true }}>
              {`${t('reservations')}: `}
              {timeSlot ? formatInteger(timeSlot.invitation_reservations_count) : 0}
            </UI.Div>
          )}

          <UI.Div sc={{ textAlign: 'right' }}>
            <UI.Button
              onClick={() => toggleTimeSlot()}
              sc={{ blank: true, color: 'gray.400', size: 'sm' }}
              aria-label={form.get(key('active')) ? t('disable') : t('enable')}
            >
              <UI.Icon>
                {form.get(key('active')) ? <Eye /> : <EyeOff />}
              </UI.Icon>
            </UI.Button>
            <UI.Button
              onClick={() => deleteTimeSlot()}
              sc={{ blank: true, color: 'gray.400', size: 'sm' }}
              disabled={timeSlot && !timeSlot.deletable}
            >
              <UI.Icon>
                <Trash2 />
              </UI.Icon>
            </UI.Button>
          </UI.Div>
        </UI.FormGrid>
      </UI.FormGrid>
    </UI.Div>
  );
};

const editTicketTimeSlotsSchema = yup.object().shape({
  time_slots: yup.array(yup.object().shape({
    start_date: yup.string().required(),
  })),
});

export interface EditTicketTimeSlotsFormProps {
  ticket: Ticket;
  event: Event;
  refetchQueries?: TypedDocumentNode[];
}

const EditTicketTimeSlotsForm = ({
  ticket, event, refetchQueries = [GetEventLayoutQuery],
}: EditTicketTimeSlotsFormProps) => {
  const { t } = useTranslation();
  const project = useProject();
  const device = useMediaDevice();
  const { parseDate, formatDate } = useLocale();

  const getValues = (ticket: Ticket) => ({
    time_slots: ticket.time_slots.map((timeSlot) => ({
      id: timeSlot.id,
      start_date: timeSlot.start_date,
      start_time: timeSlot.start_time,
      title: timeSlot.title,
      title_translations: translationsForAttribute(timeSlot.translations, 'title'),
      capacity: timeSlot.capacity,
      active: timeSlot.active,
    })),
  });

  const form = useForm({
    values: getValues(ticket),
  });

  useNavigationPrompt(form.changed());

  const timeSlotKeys = useRef(form.values.time_slots.map((value, index) => index));

  const [editTicket, { error, loading }] = useEditTicketMutation({
    input: {
      id: ticket.id,
      time_slots: form.values.time_slots,
    },
    onSuccess: (ticket) => {
      form.reset(getValues(ticket));

      // Update keys to rerender all time slots, because the order of time slots might have been updated by the server.
      const maxKey = Math.max(0, ...timeSlotKeys.current);
      timeSlotKeys.current = timeSlotKeys.current.map((value) => value + maxKey + 1);
    },
    refetchQueries,
  });

  const TimeSlotForm = device.isSmall ? MobileTimeSlotForm : DesktopTimeSlotForm;

  const { valid, errors } = useValidation({
    schema: editTicketTimeSlotsSchema,
    values: form.values,
    externalErrors: getServerErrors(error),
  });

  // Get start date if both date and time are set.
  const getStartDate = (timeSlot: TimeSlotData): Date | null => {
    if (timeSlot.start_date && timeSlot.start_time) {
      return parseDate(`${timeSlot.start_date} ${timeSlot.start_time}`);
    }

    if (timeSlot.start_date) {
      return parseDate(timeSlot.start_date, { format: 'internal_date' });
    }

    return null;
  };

  const addTimeSlot = () => {
    let date: Date;

    const currentSlots = takeRight(form.get('time_slots'), 2) as TimeSlotData[];

    if (currentSlots.length === 0) {
      date = parseDate(event.start_date, { timezone: 'UTC' });
    } else if (currentSlots.length === 1 && currentSlots[0].start_date) {
      // Assume 15 minute time slots by default
      const startDate = getStartDate(currentSlots[0]);

      if (currentSlots[0].start_time) {
        date = addMinutes(startDate, 15);
      } else {
        date = addDays(startDate, 1);
      }
    } else if (currentSlots.length === 2 && currentSlots[0].start_date && currentSlots[1].start_date) {
      // Guess the interval based on the difference in minutes between the last two time slots.
      const secondToLastDate = getStartDate(currentSlots[0]);
      const lastDate = getStartDate(currentSlots[1]);

      if (
        (currentSlots[0].start_time && currentSlots[1].start_time)
          || (!currentSlots[0].start_time && !currentSlots[1].start_time)
      ) {
        // If both time slots have a time OR if both time slots do not have a time,
        // guess the new time slot's date/time based on the difference
        const diffInMinutes = differenceInMinutes(lastDate, secondToLastDate);
        date = addMinutes(lastDate, diffInMinutes);
      }
    }

    const previousTimeSlot = currentSlots[currentSlots.length - 1];
    const hasTime = date && (!previousTimeSlot || !!previousTimeSlot.start_time);

    form.append('time_slots', {
      start_date: date ? formatDate(date, { format: 'internal_date' }) : previousTimeSlot?.start_date,
      start_time: hasTime ? formatDate(date, { format: 'internal_time' }) : null,
      title_translations: [],
      active: true,
    });

    timeSlotKeys.current.push(Math.max(0, ...timeSlotKeys.current) + 1);
  };

  const toggleTimeSlot = (index: number) => {
    form.set(`time_slots.${index}.active`, (value: boolean) => !value);
  };

  const deleteTimeSlot = (index: number) => {
    form.delete('time_slots', index);
    timeSlotKeys.current.splice(index, 1);
  };

  const timeSlots = ticket ? keyBy(ticket.time_slots, 'id') : {};

  const hasReservations = ticket?.active
    && ticket.time_slots.filter((timeSlot) => timeSlot.invitation_reservations_count > 0).length > 0;

  return (
    <UI.Form onSubmit={() => editTicket()}>
      <UI.FormGrid>
        <UI.ServerErrors error={error} />

        <UI.Div>
          <UI.Legend sc={{ mb: 1 }}>
            {t('time_slots')}
          </UI.Legend>
          <UI.InputDescription>
            {t('time_slots_description')}
          </UI.InputDescription>
        </UI.Div>

        <UI.FormGrid sc={{ gutter: 0.5 }}>
          {!device.isSmall && form.values.time_slots.length > 0 && (
            <UI.FormGrid sc={{
              columns: hasReservations ? '3fr 2fr 3fr 2fr 2fr 2fr 66px' : '3fr 2fr 3fr 2fr 2fr 66px',
              gutter: 0.5,
            }}
            >
              <UI.Div>
                <UI.InputLabel htmlFor="StartDate_0">
                  {t('start_date')}
                  {' '}
                  <UI.RequiredMark />
                </UI.InputLabel>
              </UI.Div>
              <UI.Div>
                <UI.InputLabel htmlFor="StartTime_0">
                  {t('start_time')}
                </UI.InputLabel>
              </UI.Div>
              <UI.Div>
                <UI.InputLabel htmlFor="Title_0">{t('title')}</UI.InputLabel>
              </UI.Div>
              <UI.Div>
                <UI.InputLabel htmlFor="Capacity_0">{t('capacity')}</UI.InputLabel>
              </UI.Div>
              <UI.Div>
                <UI.InputLabel>{t('registrations')}</UI.InputLabel>
              </UI.Div>
              {hasReservations && (
                <UI.Div>
                  <UI.InputLabel>{t('reservations')}</UI.InputLabel>
                </UI.Div>
              )}
            </UI.FormGrid>
          )}
          {form.values.time_slots.map((timeSlot: any, index: number) => (
            <TimeSlotForm
              form={form}
              index={index}
              ticket={ticket}
              timeSlot={timeSlots[timeSlot.id]}
              showReservations={hasReservations}
              errors={errors}
              toggleTimeSlot={() => toggleTimeSlot(index)}
              deleteTimeSlot={() => deleteTimeSlot(index)}
              key={timeSlotKeys.current[index]}
            />
          ))}

          <UI.Div>
            <UI.Button onClick={() => addTimeSlot()}>
              {t('add_time_slot')}
            </UI.Button>
          </UI.Div>
        </UI.FormGrid>

        <UI.HR />

        <UI.FormButtons
          form={form}
          submitLabel={t('save_ticket')}
          closeTo={`/${project.id}/events/${event.id}/tickets`}
          loading={loading}
          valid={valid}
        />
      </UI.FormGrid>
    </UI.Form>
  );
};

export default EditTicketTimeSlotsForm;
